import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CloseCircle } from "iconsax-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Link, SefanoButton } from "@/components";
import { TERMS_SECTIONS } from "@/lib/content/terms";
import { formatPersianNumber } from "@/lib/format/persian";
import * as styles from "./Terms.template.styles";

export function TermsTemplate() {
  const { t } = useTranslation("terms");
  const theme = useTheme();
  const [activeSection, setActiveSection] = useState(TERMS_SECTIONS[0]?.id ?? "s1");

  const scrollToSection = useCallback((id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <Box sx={styles.root}>
      <Box sx={styles.overlay(theme)} />

      <Box sx={styles.card(theme)}>
        <Box sx={styles.cardHeader}>
          <Box sx={styles.headerBrand}>
            <Box component="img" src="/images/logo.png" alt="SEFANO" sx={styles.headerLogo} />
            <Box>
              <Typography sx={styles.headerTitle}>{t("title")}</Typography>
              <Typography sx={styles.headerSubtitle}>{t("lastUpdated")}</Typography>
            </Box>
          </Box>
          <Box component={Link} to="/login" sx={styles.closeButton(theme)} aria-label={t("understood")}>
            <CloseCircle size={20} variant="Linear" color={theme.palette.text.secondary} />
          </Box>
        </Box>

        <Box sx={styles.cardBody}>
          <Box component="nav" sx={styles.sideNav}>
            {TERMS_SECTIONS.map((section, index) => (
              <Box
                key={section.id}
                component="button"
                type="button"
                sx={styles.navItem(activeSection === section.id)}
                onClick={() => scrollToSection(section.id)}
              >
                <Box sx={styles.navNumber(activeSection === section.id)}>
                  {formatPersianNumber(index + 1)}
                </Box>
                {section.navLabel}
              </Box>
            ))}
          </Box>

          <Box sx={styles.content}>
            <Typography sx={styles.intro}>{t("intro")}</Typography>

            {TERMS_SECTIONS.map((section) => {
              const SectionIcon = section.Icon;

              return (
                <Box key={section.id} id={section.id} sx={styles.section}>
                  <Typography sx={styles.sectionTitle}>
                    <SectionIcon size={18} variant="Bold" color={theme.palette.primary.main} />
                    {t(section.titleKey)}
                  </Typography>
                  <Typography sx={styles.sectionBody}>{t(section.bodyKey)}</Typography>
                </Box>
              );
            })}

            <Box sx={styles.supportBox}>
              <Typography sx={styles.supportTitle}>{t("supportTitle")}</Typography>
              <Typography sx={styles.sectionBody}>{t("supportBody")}</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.cardFooter}>
          <Typography sx={styles.copyright}>{t("copyright")}</Typography>
          <Link to="/login">
            <SefanoButton sx={{ minWidth: 120, minHeight: 42 }}>{t("understood")}</SefanoButton>
          </Link>
        </Box>
      </Box>
    </Box>
  );
}
